/**
 * Title Canvas Background Renderer（v1.1.0 屏 0 首页）
 *
 * 职责：Canvas 2D 绘制赛博指挥终端背景动画
 *   1. 战术网格（45°斜线 + 扫描线）
 *   2. 雷达（旋转扫描线 + 随机光点）
 *
 * 性能：rAF 帧内 <2ms，visibilitychange 暂停/恢复
 *
 * 使用：
 *   import { createTitleBackground } from '../render/title-canvas-background';
 *   const ctrl = createTitleBackground(canvas);
 *   ctrl.start();
 *   // 销毁时
 *   ctrl.stop();
 */

import { tokenColor, tokenColorRgba } from "../utils/color-tokens";

// ===== 动画参数（来源：spec.md） =====

/** 网格间距（px） */
const GRID_SPACING = 40;
/** 网格 alpha */
const GRID_ALPHA = 0.08;
/** 扫描线速度（px/frame） */
const SCANLINE_SPEED = 0.3;
/** 扫描线 alpha */
const SCANLINE_ALPHA = 0.15;
/** 雷达半径（px） */
const RADAR_RADIUS = 60;
/** 雷达旋转速度（弧度/frame） */
const RADAR_SPEED = 0.005;
/** 雷达光点数量 */
const RADAR_DOTS = 3;

// ===== 类型 =====

export interface TitleBgConfig {
  /** 雷达右下角边距（px），默认 40 */
  radarMargin?: number;
}

export interface TitleBgController {
  start(): void;
  stop(): void;
  resize(): void;
  pause(): void;
  resume(): void;
}

// ===== 内部类型 =====

interface ScanLine {
  y: number;
}

interface RadarDot {
  angle: number;
  radius: number; // 0 ~ RADAR_RADIUS
  speed: number;  // 径向移动速度
  alpha: number;  // 闪烁 alpha
  alphaDir: number;
}

// ===== 工厂函数 =====

export function createTitleBackground(
  canvas: HTMLCanvasElement,
  config: TitleBgConfig = {}
): TitleBgController {
  const cfg: Required<TitleBgConfig> = {
    radarMargin: config.radarMargin ?? 40,
  };

  let ctx: CanvasRenderingContext2D | null = null;
  let animId = 0;
  let paused = false;
  let frame = 0;

  // 动画状态
  let scanLine: ScanLine;
  let radarAngle = 0;
  let radarDots: RadarDot[] = [];

  // ===== 离屏缓存（drawGrid 离屏 canvas） =====
  let gridCacheCanvas: HTMLCanvasElement | null = null;
  let gridCacheColor = "";
  let gridCacheW = 0;
  let gridCacheH = 0;

  // 预计算颜色（模块级缓存失效时每帧重读）
  function refreshColors(): {
    grid: string;
    radar: string;
  } {
    return {
      grid: tokenColorRgba("--tactical-cyan", GRID_ALPHA, "#00E4FF"),
      radar: tokenColor("--tactical-cyan", "#00E4FF"),
    };
  }

  let colors = refreshColors();

  function initContext(): boolean {
    if (!canvas) return false;
    ctx = canvas.getContext("2d");
    return ctx !== null;
  }

  function initState(): void {
    if (!canvas) return;
    scanLine = { y: 0 };
    radarAngle = 0;
    radarDots = Array.from({ length: RADAR_DOTS }, () => ({
      angle: Math.random() * Math.PI * 2,
      radius: 10 + Math.random() * (RADAR_RADIUS - 10),
      speed: 0.05 + Math.random() * 0.1,
      alpha: 0.3 + Math.random() * 0.7,
      alphaDir: Math.random() > 0.5 ? 0.01 : -0.01,
    }));
    frame = 0;
  }

  // ===== 绘制层级 =====

  function drawGrid(): void {
    if (!ctx || !canvas) return;
    // 离屏缓存命中则直接 drawImage
    if (
      gridCacheCanvas &&
      gridCacheColor === colors.grid &&
      gridCacheW === canvas.width &&
      gridCacheH === canvas.height
    ) {
      ctx.drawImage(gridCacheCanvas, 0, 0);
      return;
    }

    // 重建离屏缓存
    const off = document.createElement("canvas");
    off.width = canvas.width;
    off.height = canvas.height;
    const octx = off.getContext("2d");
    if (!octx) return;

    const w = canvas.width;
    const h = canvas.height;
    const c = colors.grid;

    octx.save();
    octx.strokeStyle = c;
    octx.lineWidth = 1;

    // 45度斜线网格：从左上到右下
    const diag = Math.ceil((w + h) / GRID_SPACING);
    for (let i = -diag; i < diag; i++) {
      const offset = i * GRID_SPACING;
      octx.beginPath();
      octx.moveTo(offset, 0);
      octx.lineTo(offset - h, h);
      octx.stroke();
    }

    octx.restore();

    gridCacheCanvas = off;
    gridCacheColor = colors.grid;
    gridCacheW = w;
    gridCacheH = h;

    ctx.drawImage(off, 0, 0);
  }

  function drawScanline(): void {
    if (!ctx || !canvas) return;
    const h = canvas.height;
    const w = canvas.width;
    const c = tokenColorRgba("--tactical-cyan", SCANLINE_ALPHA * 0.5, "#00E4FF");

    scanLine.y = (scanLine.y + SCANLINE_SPEED) % h;

    ctx.save();
    ctx.strokeStyle = c;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, scanLine.y);
    ctx.lineTo(w, scanLine.y);
    ctx.stroke();
    ctx.restore();
  }

  function drawRadar(): void {
    if (!ctx || !canvas) return;
    const w = canvas.width;
    const h = canvas.height;
    const cx = w - cfg.radarMargin - RADAR_RADIUS;
    const cy = h - cfg.radarMargin - RADAR_RADIUS;

    radarAngle = (radarAngle + RADAR_SPEED) % (Math.PI * 2);

    ctx.save();

    // 雷达底图（圆环）
    ctx.strokeStyle = tokenColorRgba("--tactical-cyan", 0.15, "#00E4FF");
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(cx, cy, RADAR_RADIUS, 0, Math.PI * 2);
    ctx.stroke();

    // 内圈
    ctx.beginPath();
    ctx.arc(cx, cy, RADAR_RADIUS * 0.5, 0, Math.PI * 2);
    ctx.stroke();

    // 十字线
    ctx.beginPath();
    ctx.moveTo(cx - RADAR_RADIUS, cy);
    ctx.lineTo(cx + RADAR_RADIUS, cy);
    ctx.moveTo(cx, cy - RADAR_RADIUS);
    ctx.lineTo(cx, cy + RADAR_RADIUS);
    ctx.stroke();

    // 扫描线
    ctx.strokeStyle = tokenColorRgba("--tactical-cyan", 0.4, "#00E4FF");
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(
      cx + Math.cos(radarAngle) * RADAR_RADIUS,
      cy + Math.sin(radarAngle) * RADAR_RADIUS
    );
    ctx.stroke();

    // 随机光点
    for (const dot of radarDots) {
      // 闪烁 alpha
      dot.alpha += dot.alphaDir;
      if (dot.alpha >= 1 || dot.alpha <= 0.1) dot.alphaDir *= -1;
      // 径向移动
      dot.radius += dot.speed;
      if (dot.radius >= RADAR_RADIUS - 3 || dot.radius <= 5) dot.speed *= -1;

      const dx = cx + Math.cos(dot.angle) * dot.radius;
      const dy = cy + Math.sin(dot.angle) * dot.radius;

      ctx.fillStyle = `rgba(0,228,255,${dot.alpha})`;
      ctx.beginPath();
      ctx.arc(dx, dy, 2, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }

  // ===== 主循环 =====

  function render(): void {
    if (!ctx || !canvas || paused) {
      animId = 0;
      return;
    }

    frame++;

    // 每 60 帧刷新颜色（主题切换时生效）
    if (frame % 60 === 1) {
      colors = refreshColors();
    }

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // 第 1 层：网格
    drawGrid();
    // 第 2 层：扫描线（鼠标邻近弯曲）
    drawScanline();
    // 第 3 层（最前）：雷达
    drawRadar();

    animId = requestAnimationFrame(render);
  }

  function handleVisibility(): void {
    if (document.hidden) {
      pause();
    } else {
      resume();
    }
  }

  // ===== 公开 API =====

  function start(): void {
    if (!initContext()) return;
    initState();
    document.addEventListener("visibilitychange", handleVisibility);
    animId = requestAnimationFrame(render);
  }

  function stop(): void {
    cancelAnimationFrame(animId);
    animId = 0;
    document.removeEventListener("visibilitychange", handleVisibility);
    ctx = null;
    gridCacheCanvas = null;
    gridCacheColor = "";
    gridCacheW = 0;
    gridCacheH = 0;
  }

  function resize(): void {
    if (!canvas) return;
    const parent = canvas.parentElement;
    if (!parent) return;
    const rect = parent.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    canvas.style.width = `${rect.width}px`;
    canvas.style.height = `${rect.height}px`;
    if (ctx) {
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
    // resize 后离屏缓存失效，下次 drawGrid 会自动重建
    gridCacheCanvas = null;
    gridCacheColor = "";
    gridCacheW = 0;
    gridCacheH = 0;
  }

  function pause(): void {
    paused = true;
    cancelAnimationFrame(animId);
    animId = 0;
  }

  function resume(): void {
    if (!paused) return;
    paused = false;
    if (initContext()) {
      animId = requestAnimationFrame(render);
    }
  }

  return { start, stop, resize, pause, resume };
}
